import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { destinoPagoService } from '../services/destinoPagoService';
import type { DestinoPago, DestinoPagoFormData } from '../types';
import { aplicarOrden } from '../utils/orden';

export const DESTINOS_PAGO_QUERY_KEY = ['presupuestos', 'destinos-pago'] as const;
const LISTADO_KEY = [...DESTINOS_PAGO_QUERY_KEY, 'list'] as const;
const OPCIONES_KEY = [...DESTINOS_PAGO_QUERY_KEY, 'opciones'] as const;

/**
 * Administración de los destinos de pago (Configuración del Sistema).
 */
export const useDestinosPago = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: LISTADO_KEY,
    queryFn: destinoPagoService.listar,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: DESTINOS_PAGO_QUERY_KEY });

  const crearMutation = useMutation({
    mutationFn: (payload: DestinoPagoFormData) => destinoPagoService.crear(payload),
    onSuccess: invalidar,
  });

  const actualizarMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DestinoPagoFormData> }) =>
      destinoPagoService.actualizar(id, data),
    onSuccess: invalidar,
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => destinoPagoService.eliminar(id),
    onSuccess: invalidar,
  });

  const marcarDefaultMutation = useMutation({
    mutationFn: (id: string) => destinoPagoService.marcarDefault(id),
    onSuccess: invalidar,
  });

  const reordenarMutation = useMutation({
    mutationFn: (ids: string[]) => destinoPagoService.reordenar(ids),
    // Si no se guardó, vuelve el orden del servidor; si se guardó, se refrescan
    // también las opciones de los selectores de ítems.
    onSettled: invalidar,
  });

  /**
   * Guarda el orden arrastrado. Se aplica en la caché antes de la request para
   * que la lista no vuelva un instante al orden anterior.
   */
  const reordenar = (ids: string[]) => {
    queryClient.setQueryData<DestinoPago[] | undefined>(LISTADO_KEY, (actual) =>
      actual ? aplicarOrden(actual, ids) : actual
    );

    return reordenarMutation.mutateAsync(ids);
  };

  return {
    destinos: data ?? [],
    isLoading,
    error,
    crearDestino: crearMutation.mutateAsync,
    actualizarDestino: actualizarMutation.mutateAsync,
    eliminarDestino: eliminarMutation.mutateAsync,
    marcarDefault: marcarDefaultMutation.mutateAsync,
    reordenar,
    isGuardando:
      crearMutation.isPending ||
      actualizarMutation.isPending ||
      eliminarMutation.isPending ||
      marcarDefaultMutation.isPending ||
      reordenarMutation.isPending,
  };
};

/**
 * Opciones de destino para los formularios de ítems. El default es con el
 * que arranca un ítem nuevo.
 */
export const useDestinosPagoOpciones = ({ enabled = true } = {}) => {
  const { data, isLoading } = useQuery({
    queryKey: OPCIONES_KEY,
    queryFn: destinoPagoService.opciones,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const opciones = data ?? [];

  return {
    opciones,
    destinoPorDefecto: opciones.find((opcion) => opcion.esDefault) ?? null,
    isLoading,
  };
};
